import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
// TEMPORARILY DISABLED STRIPE
// import {
//   loadStripe,
//   Stripe,
//   StripeElements,
//   Appearance,
//   StripePaymentElementOptions,
//   StripeElementsOptions
// } from '@stripe/stripe-js';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../services/logger.service';

interface Plan {
  plan: string;
  price: number;
  features: string[];
}

interface FeatureHighlight {
  icon: string;
  title: string;
  description: string;
}

interface RecentFeature extends FeatureHighlight {
  released: string;
}

interface FAQ {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-plan-selection',
  templateUrl: './plan-selection.component.html',
  styleUrls: ['./plan-selection.component.css']
})
export class PlanSelectionComponent implements OnInit, OnDestroy {
  plans: Plan[] = [
    {
      plan: "Professional",
      price: 499,
      features: [
        "Unlimited students",
        "Advanced scheduling & recurring classes",
        "Smart double-booking prevention for lessons & instructors",
        "SMS & email notifications",
        "Advanced reporting & analytics",
        "Mobile friendly",
        "Multiple instructor management",
        "Square payments with terminal support",
        "Stripe payment processing",
        "Direct Arthur Murray report submissions",
        "Customizable international tax support",
        "Email & phone support",
        "Excel spreadsheet import tools",
        "FloatingRain & iDance Studios migration assistance*",
        // "Customizable forms & waivers"
      ]
    }
  ];

  features: FeatureHighlight[] = [
    {
      icon: 'calendar-alt',
      title: 'Smart Scheduling',
      description: 'Easily manage classes, private lessons, and studio space with our intuitive calendar.'
    },
    {
      icon: 'credit-card',
      title: 'Flexible Payments',
      description: 'Accept payments through Square terminal or Stripe online processing.'
    },
    {
      icon: 'chart-line',
      title: 'Franchise Reports',
      description: 'Automatically generate and submit required Arthur Murray reports.'
    },
    {
      icon: 'headset',
      title: 'Email & phone support',
      description: 'Contact our team for assistance with your studio management system.'
    },
    {
      icon: 'users',
      title: 'Student Management',
      description: 'Track student progress, attendance records, and manage lesson packages.'
    },
    {
      icon: 'mobile-alt',
      title: 'Mobile Friendly',
      description: 'Access your studio management system from any device, anywhere.'
    },
    {
      icon: 'bell',
      title: 'Smart Notifications',
      description: 'Automated SMS and email reminders for classes and appointments.'
    },
    {
      icon: 'file-import',
      title: 'Easy Migration',
      description: 'Seamlessly import data from spreadsheets or other dance studio software.'
    }
  ];

  // Major releases from the last 12 months; the first one is shown as the featured card
  recentFeatures: RecentFeature[] = [
    {
      icon: 'calendar-check',
      title: 'Online Booking',
      description: "Prospective students book their first lesson straight from your studio's page on arthurmurray.com. Web bookings land on the Day View in their own Website Booking column, and confirmations go out by email and text.",
      released: 'Rolling out now'
    },
    {
      icon: 'gift',
      title: 'Gift Certificates',
      description: 'Sell gift certificates by email or print, schedule delivery for a later date, and let recipients check their balance online.',
      released: 'Sep 2026'
    },
    {
      icon: 'cash-register',
      title: 'PayBright Payments',
      description: 'Take card payments on a PayBright terminal, keep cards on file for repeat charges, and refund without leaving the app.',
      released: 'Jun 2026'
    },
    {
      icon: 'calculator',
      title: 'QuickBooks Sync',
      description: "Connect your studio's QuickBooks account and keep your sales in sync automatically.",
      released: 'Aug 2026'
    },
    {
      icon: 'repeat',
      title: 'Recurring Lessons',
      description: "Make any lesson recurring in one click. Series run to a set date or indefinitely, tied to the student's enrollment.",
      released: 'Feb 2026'
    },
    {
      icon: 'language',
      title: '26 Languages',
      description: 'The app, receipts and emails in 26 languages, with local date formats, regional taxes and country-specific enrollment agreements.',
      released: 'Oct 2026'
    },
    {
      icon: 'clock-rotate-left',
      title: 'Change Log',
      description: 'See who changed what on payments, packages, student accounts, contacts and studio settings.',
      released: 'Sep 2026'
    },
    {
      icon: 'database',
      title: 'Export & Backup',
      description: "Download your studio's data whenever you need it, all from one page.",
      released: 'May 2026'
    }
  ];

  faqs: FAQ[] = [
    {
      question: 'How long does it take to set up?',
      answer: 'Setup typically takes up to a week, depending on how many active students your studio has. In rare cases, it might take longer for studios with extensive data. Our team will help you import your existing data and provide personalized training for your staff.'
    },
    {
      question: 'Can I migrate my data from another system?',
      answer: 'Yes, we offer migration assistance from FloatingRain, iDance Studios, and Excel spreadsheets. Our team will help ensure a smooth transition.'
    },
    {
      question: 'Do you offer a free trial?',
      answer: 'We offer a personalized demo of the platform. Since many features require custom setup, we work with you to ensure the system meets your needs before you commit.'
    }
  ];

  // TEMPORARILY DISABLED STRIPE
  // private stripe: Stripe | null = null;
  // elements: StripeElements | null = null;
  private stripe: any = null;
  elements: any = null;
  clientSecret: string | null = null;
  loading = false;
  errorMessage = '';
  messageTimeout: any;

  constructor(private router: Router, private http: HttpClient) {}

  ngOnInit() {
    // Initialize will happen when the user clicks "Get Started"
    this.scrollToElementOnLoad();
  }

  // Scroll to section if hash is present in URL
  private scrollToElementOnLoad() {
    setTimeout(() => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 100);
  }

  async onSelectPlan(plan: Plan) {
    this.loading = true;

    try {
      await this.initialize();
    } catch (error) {
      this.showMessage('Failed to initialize payment system.');
      LoggerService.error('Initialization error:', error);
    } finally {
      this.loading = false;
    }
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private async initialize() {
    // TEMPORARILY DISABLED STRIPE
    LoggerService.log('[STRIPE] Payment initialization temporarily disabled');
    this.showMessage("Payment system is temporarily disabled for maintenance.");
    return;
    
    // // Initialize Stripe
    // this.stripe = await loadStripe(environment.stripePublicKey);

    // if (!this.stripe) {
    //   this.showMessage("Failed to initialize Stripe.");
    //   return;
    // }

    // try {
    //   // Create a subscription intent on the server
    //   const { clientSecret } = await this.http.post<{ clientSecret: string }>(
    //     '/api/create-subscription',
    //     { priceId: 'prod_RlmhLkQVEVxpbi' }
    //   ).toPromise();

    //   this.clientSecret = clientSecret;

    //   // Initialize Elements with modern appearance
    //   const elementsOptions: StripeElementsOptions = {
    //     clientSecret,
    //     appearance: {
    //       theme: 'stripe',
    //       variables: {
    //         colorPrimary: '#4361ee',
    //         colorBackground: '#ffffff',
    //         colorText: '#2b2d42',
    //         fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
    //         borderRadius: '12px',
    //         spacingUnit: '4px',
    //         spacingGridRow: '8px',
    //       },
    //       rules: {
    //         '.Input': {
    //           border: '1px solid #e0e0e0',
    //           boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    //         },
    //         '.Input:focus': {
    //           boxShadow: '0 0 0 2px rgba(67, 97, 238, 0.3)',
    //         },
    //         '.Label': {
    //           fontWeight: '600',
    //         }
    //       }
    //     }
    //   };

    //   this.elements = this.stripe.elements(elementsOptions);

    //   // Create and mount the Payment Element with enhanced styling
    //   const paymentElementOptions: StripePaymentElementOptions = {
    //     layout: {
    //       type: 'accordion',
    //       defaultCollapsed: false,
    //       radios: true,
    //       spacedAccordionItems: true
    //     }
    //   };

    //   const paymentElement = this.elements.create('payment' as any, paymentElementOptions);
    //   paymentElement.mount("#payment-element");

    // } catch (error) {
    //   this.showMessage('Failed to initialize payment system.');
    //   LoggerService.error('Initialization error:', error);
    //   throw error; // Re-throw to be caught by onSelectPlan
    // }
  }

  async handleSubmit(event: Event) {
    event.preventDefault();
    
    // TEMPORARILY DISABLED STRIPE
    this.showMessage("Payment processing is temporarily disabled.");
    return;

    // if (!this.stripe || !this.elements) {
    //   return;
    // }

    // this.setLoading(true);

    // try {
    //   const { error } = await this.stripe.confirmPayment({
    //     elements: this.elements,
    //     confirmParams: {
    //       return_url: `${window.location.origin}/subscription/complete`,
    //     },
    //   });

    //   // This point will only be reached if there is an immediate error when
    //   // confirming the payment. Otherwise, your customer will be redirected to
    //   // your `return_url`.
    //   if (error.type === "card_error" || error.type === "validation_error") {
    //     this.showMessage(error.message || "An error occurred.");
    //   } else {
    //     this.showMessage("An unexpected error occurred.");
    //   }
    // } finally {
    //   this.setLoading(false);
    // }
  }

  // UI helpers
  private showMessage(messageText: string) {
    this.errorMessage = messageText;

    // Clear any existing timeout
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    // Hide the message after 4 seconds
    this.messageTimeout = setTimeout(() => {
      this.errorMessage = '';
    }, 4000);
  }

  private setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  // Clean up on component destruction
  ngOnDestroy() {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }
}

